<template>
  <div id="homeWrap" class="text-left">
    <v-container grid-list-md fluid>
      <v-layout wrap justify-space-between>
        <v-flex xs12>
          <v-layout wrap>
            <v-flex xs12 class="dropdown-widget">
              <drop-down-widget
                :title="widgetTitle[0]"
                :number-of-rows="
                  saveFavWidgetHomes ? saveFavWidgetHomes.length : 0
                "
                :header-style="widgetStyles[0]"
                :is-open="widgetState[0].isOpen"
                @on-open="widgetExpand(0)"
              >
                <template v-if="clientFavorites" v-slot:widget-content>
                  <saved-favorites-widget
                    :table-data="saveFavWidgetHomes"
                    :options="{ hideActions: true }"
                  />
                </template>
              </drop-down-widget>
            </v-flex>

            <v-flex xs12 class="dropdown-widget">
              <drop-down-widget
                :title="widgetTitle[1]"
                :number-of-rows="yourScheduleTotalRows"
                :header-style="widgetStyles[1]"
                :is-open="widgetState[1].isOpen"
                @on-open="widgetExpand(1)"
              >
                <!-- The component has to be wrapped in a template. -->
                <template v-slot:widget-content>
                  <your-schedule-widget :table-data="clientScheduleData" />
                </template>
              </drop-down-widget>
            </v-flex>

            <!-- FEEDBACK MATCH -->
            <v-flex xs12 class="dropdown-widget">
              <drop-down-widget
                :title="widgetTitle[2]"
                :number-of-rows="
                  highMatchFeedbackHomes ? highMatchFeedbackHomes.length : 0
                "
                :header-style="widgetStyles[2]"
                :is-open="widgetState[2].isOpen"
                @on-open="widgetExpand(2)"
              >
                <!-- The component has to be wrapped in a template. -->
                <template v-slot:widget-content>
                  <feedback-match-widget
                    :table-data="highMatchFeedbackHomes"
                    :client-data="clientData"
                    :client-favorites="clientFavorites"
                  />
                </template>
              </drop-down-widget>
            </v-flex>

            <!-- PROVIDE FEEDBACK -->
            <v-flex xs12 class="dropdown-widget">
              <drop-down-widget
                :title="widgetTitle[3]"
                :number-of-rows="
                  feedbackNeededHomes ? feedbackNeededHomes.length : 0
                "
                :header-style="widgetStyles[3]"
                :is-open="widgetState[3].isOpen"
                @on-open="widgetExpand(3)"
              >
                <!-- The component has to be wrapped in a template. -->
                <template v-slot:widget-content>
                  <feedback-match-widget
                    :table-data="feedbackNeededHomes"
                    :client-data="clientData"
                    :client-favorites="clientFavorites"
                    :match-data="false"
                  />
                </template>
              </drop-down-widget>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs12 sm3 md4>
          <!-- Sidebar -->
          <v-slide-x-reverse-transition>
            <client-detail-card
              v-show="showClientCard"
              :home-buying-stages="homeBuyingStages"
            >
              <template v-slot:close-button>
                <div class="button-close" @click="showClientCard = false">
                  <v-icon class="button-close-icon">
                    close
                  </v-icon>
                </div>
              </template>
            </client-detail-card>
          </v-slide-x-reverse-transition>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import { homeBuyingStages } from '../../components/client-detail/client-fake-data.js'

import SavedFavoritesWidget from '../../components/client/dashboard/SavedFavoritesWidget.vue'
import YourScheduleWidget from '../../components/client/dashboard/YourScheduleWidget.vue'
import FeedbackMatchWidget from '../../components/client/dashboard/FeedbackMatchWidget.vue'
import DropDownWidget from '../../components/home/DropDownWidget.vue'
import ClientDetailCard from '../../components/client-detail/ClientDetailCard.vue'

import refreshClientData from '../../components/mixins/refreshClientData'

import {
  calculateFeedbackMatch,
  sortByRequestedTourTimestamp
} from '../../utils/propertyUtils'
import colors from '../../scss/_colors.scss'
import { pastScheduledProperties } from '../../utils/routeUtils'
import { HIDE_STATUSES } from '../../components/common/searchPropertyConst'
import { isEmpty } from '../../utils/generalUtils'

export default {
  name: 'Home',
  components: {
    SavedFavoritesWidget,
    YourScheduleWidget,
    FeedbackMatchWidget,
    DropDownWidget,
    ClientDetailCard
  },

  mixins: [refreshClientData],

  data() {
    return {
      showClientCard: false, // used for toggling the sidebar with client info
      clientDetailForCard: {},
      homeBuyingStages,
      widgetTitle: [
        'SAVED FAVORITES',
        'YOUR SCHEDULE',
        'FEEDBACK MATCH',
        'PROVIDE FEEDBACK'
      ],
      widgetStyles: [
        {
          'background-color': colors.hwayzBrandBlue,
          color: 'white',
          'font-weight': '400'
        },
        {
          'background-color': colors.hwayzBrandGold,
          color: 'white',
          'font-weight': '400'
        },
        {
          'background-color': colors.hwayzPurple,
          color: 'white',
          'font-weight': '400'
        },
        {
          'background-color': colors.hwayzKellyGreen,
          color: 'white',
          'font-weight': '400'
        }
      ],
      feedbackHomes: [],
      widgetState: [0, 1, 2, 3].map(position => ({ isOpen: -1 }))
    }
  },

  computed: {
    ...mapState('client', [
      'clientData',
      'clientRouteData',
      'savedFavWidgetRequestTracker',
      'sortedPropertyIds'
    ]),
    ...mapGetters('client', ['feedbackNeededHomes', 'feedbackProvidedHomes']),

    saveFavWidgetHomes() {
      if (this.clientData) {
        let touredPropertyIds = pastScheduledProperties(this.clientData.routes)
        const filteredHomes = this.clientFavorites
          .filter(home => !touredPropertyIds.includes(home.propertyId))
          .filter(data => !HIDE_STATUSES.includes(data.Status))
        const { properties, sortedProperties } = sortByRequestedTourTimestamp(
          this.clientData,
          filteredHomes,
          this.savedFavWidgetRequestTracker,
          this.sortedPropertyIds
        )
        if (!isEmpty(sortedProperties)) {
          this.setSortedPropertyIds(sortedProperties.map(home => home._id))
        }
        return properties
      }
      return []
    },

    clientHomes() {
      if (this.clientData) {
        let touredPropertyIds = pastScheduledProperties(this.clientData.routes)
        return this.clientData.homes.filter(
          home => !touredPropertyIds.includes(home.propertyId)
        )
      }
      return []
    },

    yourScheduleTotalRows() {
      return this.clientScheduleData.length || 0
    },

    highMatchFeedbackHomes() {
      return this.feedbackProvidedHomes.filter(
        data => Math.round(this.getMatchPercent(data)) / 10 >= 8
      )
    },

    clientScheduleData() {
      return this.clientRouteData.filter(
        route =>
          (route.state === 'scheduled' || route.state === 'cancelled') &&
          (route.startTime >
            moment(new Date())
              .utc()
              .format() ||
            moment(route.startTime).isSame(moment(), 'day'))
      )
    }
  },

  methods: {
    ...mapMutations('client', {
      setSortedPropertyIds: 'SET_SORTED_PROPERTY_IDS'
    }),
    ...mapActions('client', ['removeFromFavoritesAction']),

    getMatchPercent(home) {
      if (home.feedback.match) {
        return calculateFeedbackMatch(home.feedback.match)
      }
      return 'N/A'
    },

    widgetExpand(position) {
      this.widgetState.forEach((item, index) => {
        item.isOpen = position === index ? 0 : -1
      })
    }
  }
}
</script>

<style lang="sass" scoped>
#homeWrap
  h3, p
    padding-left: 15px
  .container.fluid
    padding: 5px 5px 0 5px !important

.dropdown-widget
  margin: 0 6px

.no-padding
  padding: 0 !important

.button-close
  position: absolute
  top: 5px
  right: 10px
  cursor: pointer

.button-close-icon
  color: #a1a1a1 !important
  font-size: 15px
</style>
